import axios from 'axios'
import Service from '@/api/service'
import getFormDataFromJSON from '../functions/formdata'

axios.defaults.baseURL = process.env.VUE_APP_API_URL

/*
    using '...Service('/url/:id/')' in object provides methods:
        fetch
        get
        delete
        put
        save
 */

export const Room = {
  ...Service('/chat/rooms/:id/'),
  directMessage(userId) {
    const params = {
      user: userId
    }
    return axios.get(`/chat/rooms/direct/`, { params })
  }
}

export const Message = {
  ...Service('/chat/messages/:id/'),
  markAsRead(id) {
    return axios.put(`/chat/messages/${id}/mark_as_read/`)
  }
}

export const Auth = {
  ...Service('/users/auth/users/:id/'),
  me() {
    return axios.get('/users/auth/users/me/')
  },
  remind(payload) {
    return axios.post('/users/auth/users/reset_password/', payload)
  },
  login(payload) {
    return axios.post('/users/auth/token/login/', payload)
  },
  changePassword(payload) {
    return axios.post('/users/auth/users/set_password/', payload)
  },
  logout() {
    return axios.post('/users/auth/token/logout/')
  },
  resetPassword(payload) {
    return axios.post('/users/auth/users/reset_password_confirm/', payload)
  }
}

export const Lecturer = {
  ...Service('/lecturers/:id/'),
  attachmentUploadUrl: axios.defaults.baseURL + '/lecturers/attachment_upload/',
  deactivate(lecturerId) {
    return axios.post(`/lecturers/${lecturerId}/deactivate/`)
  },
  activate(lecturerId) {
    return axios.post(`/lecturers/${lecturerId}/activate/`)
  },
  report: ({ lecturerId, params }) => {
    const url = `/lecturers/${lecturerId}/report/`
    return axios.get(url, {
      params,
      responseType: 'blob'
    })
  }
}

export const ReservedSlot = {
  fetch(urlParams, payload) {
    return axios.get('/reservations/slots/', payload)
  }
}

export const LanguagesTaught = {
  ...Service('/lecturers/languages/:id/')
}

export const Application = {
  ...Service('/learning-materials/applications/:id/')
}

export const Note = {
  ...Service('/notes/:id/')
}

export const Material = {
  ...Service('/learning-materials/materials/:id/')
}

export const Settlement = {
  ...Service('/payments/settlements/:id/'),
  patchMultipart(id, payload) {
    const url = `/payments/settlements/${id}/`
    const formData = getFormDataFromJSON(payload)
    return axios.patch(url, formData, {
      headers: { 'content-type': 'multipart/form-data' }
    })
  }
}

export const CoursePayment = {
  ...Service('/payments/course-payments/:id/'),
  patchMultipart(id, payload) {
    const url = `/payments/course-payments/${id}/`
    const formData = getFormDataFromJSON(payload)
    return axios.patch(url, formData, {
      headers: { 'content-type': 'multipart/form-data' }
    })
  }
}

export const Agreement = {
  ...Service('/payments/agreements/:id/'),
  postMultipart(payload) {
    const url = `/payments/agreements/`
    const formData = getFormDataFromJSON(payload)
    return axios.post(url, formData, {
      headers: { 'content-type': 'multipart/form-data' }
    })
  }
}

export const Course = {
  ...Service('/courses/:id/')
}

export const Student = {
  ...Service('/students/:id/'),
  attachmentUploadUrl: axios.defaults.baseURL + '/students/attachment_upload/'
}

export const Reservation = {
  ...Service('/reservations/:id/'),
  confirm(reservationId) {
    return axios.post(`/reservations/${reservationId}/confirm/`)
  },
  previewChanges(payload) {
    return axios.post(`/reservations/preview_changes/`, payload)
  },
  decline(reservationId) {
    return axios.post(`/reservations/${reservationId}/decline/`)
  }
}

export const ReservationSlot = {
  cancel({ slotId, every = false, cancellation_reason = '' }) {
    const url = `/reservations/slot-cancel/`
    return axios.post(url, {
      slot_clicked: slotId,
      every: every,
      cancellation_reason
    })
  },
  cancelDetails(cancellationId) {
    const url = `/reservations/slot-cancel/${cancellationId}`
    return axios.get(url)
  },
  toggleIsExam(reservedSlotId) {
    return axios.put(`/reservations/toggle-is-exam/${reservedSlotId}/`)
  }
}

export const Availability = {
  ...Service('/availabilities/:id/'),
  cancel({ availabilityId, timespan, every = false }) {
    const url = `/availabilities/${availabilityId}/cancel/`
    return axios.put(url, {
      timespan,
      every
    })
  }
}

export const Notification = {
  ...Service('/notifications/:id/'),
  count() {
    return axios.get('/notifications/count/')
  },
  markAsRead(id) {
    return axios.put(`/notifications/${id}/mark-as-read/`)
  },
  bulkMarkAsRead() {
    return axios.put('/notifications/bulk-mark-as-read/')
  }
}

export const Presence = {
  list() {
    const url = '/availabilities/presence/'
    return axios.get(url)
  },
  confirm(reservedSlotId) {
    const url = `/availabilities/presence/${reservedSlotId}/confirm/`
    return axios.post(url)
  },
  deny(reservedSlotId, reason) {
    const url = `/availabilities/presence/${reservedSlotId}/deny/`
    return axios.post(url, { reason })
  }
}

export const Slot = {
  ...Service('/availabilities/slots/:id/'),
  cancel({ slotId, every = false }) {
    const url = `/availabilities/slots/${slotId}/cancel/`
    return axios.put(url, {
      slot: slotId,
      every
    })
  }
}

export const Language = {
  ...Service('/languages/:id/'),
  levels() {
    return axios.get('/languages/levels/')
  }
}

export const Stats = {
  fetch() {
    return axios.get('/stats/')
  }
}
